import { useState } from "react";
import { FaPlus, FaKey, FaTrashAlt, FaSync, FaUserPlus, FaUsers, FaExclamationTriangle } from "react-icons/fa";
import { ListSkeleton, EmptyState, Modal, Drawer, ListView, ListToolbar } from "../../shared";
import { Pagination, TextInput } from "../../../Components/atoms";
import { useDebounce } from "../../../Hooks/useDebounce";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetEmqxUsersQuery,
  useAddEmqxUserMutation,
  useUpdateEmqxUserPasswordMutation,
  useDeleteEmqxUserMutation,
} from "../../../store";

const PAGE_SIZE = 50;

export default function UsersTab() {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const token = auth?.accessToken;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const [addOpen, setAddOpen] = useState(false);
  const [pwdRotate, setPwdRotate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useGetEmqxUsersQuery({
    token,
    page,
    limit: PAGE_SIZE,
    like_user_id: debouncedSearch || undefined,
  });

  const [addUser, { isLoading: isAdding }] = useAddEmqxUserMutation();
  const [updatePwd, { isLoading: isRotating }] = useUpdateEmqxUserPasswordMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteEmqxUserMutation();

  const users = response?.data?.data || [];
  const meta = response?.data?.meta || {};
  const totalCount = meta.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleAdd = async ({ user_id, password }) => {
    try {
      await addUser({ token, user_id, password }).unwrap();
      showSuccess(`Kullanıcı "${user_id}" oluşturuldu`);
      setAddOpen(false);
      refetch();
    } catch (err) {
      showError(err?.data?.message || "Kullanıcı oluşturulamadı");
    }
  };

  const handleRotate = async (password) => {
    try {
      await updatePwd({ token, user_id: pwdRotate, password }).unwrap();
      showSuccess(`"${pwdRotate}" şifresi güncellendi`);
      setPwdRotate(null);
    } catch (err) {
      showError(err?.data?.message || "Şifre güncellenemedi");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser({ token, user_id: deleteConfirm }).unwrap();
      showSuccess(`"${deleteConfirm}" silindi`);
      setDeleteConfirm(null);
      refetch();
    } catch (err) {
      showError(err?.data?.message || "Kullanıcı silinemedi");
    }
  };

  // Şifre değiştir / sil aksiyonları — tablo hücresi ve mobil kartta ortak
  const renderUserActions = (u) => (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => setPwdRotate(u.user_id)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm transition-colors"
        title="Şifre Değiştir"
      >
        <FaKey className="w-3 h-3" />
        Şifre
      </button>
      <button
        type="button"
        onClick={() => setDeleteConfirm(u.user_id)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm transition-colors"
        title="Sil"
      >
        <FaTrashAlt className="w-3 h-3" />
        Sil
      </button>
    </div>
  );

  const superuserBadge = (u) =>
    u.is_superuser ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-medium">
        Superuser
      </span>
    ) : null;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      <ListToolbar
        className="px-3 pt-3 pb-2.5 sm:px-4 sm:pt-4 sm:pb-4 md:px-6"
        count={totalCount}
        countLabel="Kullanıcı"
        countIcon={FaUsers}
        isLoading={isFetching}
        actionItems={[
          {
            label: "Yeni Kullanıcı",
            mobileLabel: "Yeni EMQX Kullanıcısı Oluştur",
            icon: FaPlus,
            onClick: () => setAddOpen(true),
            variant: "primary",
          },
          {
            label: "Yenile",
            icon: FaSync,
            onClick: refetch,
            disabled: isFetching,
          },
        ]}
        searchFields={[
          {
            placeholder: "User ID ara...",
            value: search,
            onChange: (e) => {
              setSearch(e.target.value);
              setPage(1);
            },
          },
        ]}
        onClear={() => {
          setSearch("");
          setPage(1);
        }}
      />

      <ListView
        columns={[
          {
            key: "user_id",
            label: "User ID",
            cellClassName: "font-mono text-xs text-gray-900",
          },
          {
            key: "is_superuser",
            label: "Superuser",
            render: (u) =>
              u.is_superuser ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-medium">
                  Evet
                </span>
              ) : (
                <span className="text-xs text-gray-500">Hayır</span>
              ),
          },
          {
            key: "_actions",
            label: "İşlem",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: renderUserActions,
          },
        ]}
        data={users}
        rowKey="user_id"
        renderMobileCard={(u) => (
          <div className="bg-white px-3.5 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="font-mono text-xs font-semibold text-gray-900 truncate">
                {u.user_id}
              </span>
              {superuserBadge(u)}
            </div>
            {renderUserActions(u)}
          </div>
        )}
        isLoading={isLoading}
        loadingNode={
          <div className="relative p-3 sm:p-4 md:p-6">
            <ListSkeleton message="Kullanıcılar yükleniyor..." />
          </div>
        }
        empty={
          <div className="p-4 md:p-6">
            <EmptyState
              title="Kullanıcı yok"
              description={
                debouncedSearch
                  ? "Bu arama kriterine uyan kullanıcı bulunamadı."
                  : "EMQX'te kayıtlı kullanıcı bulunmuyor. Yeni kullanıcı oluşturmak için yukarıdaki butonu kullan."
              }
            />
          </div>
        }
        footer={
          totalPages > 1 ? (
            <div className="shrink-0 border-t border-gray-200 bg-white">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={PAGE_SIZE}
                totalItems={totalCount}
                showInfo={true}
                disabled={isFetching}
              />
            </div>
          ) : null
        }
      />

      <AddUserDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
        isSubmitting={isAdding}
      />
      <RotatePasswordModal
        userId={pwdRotate}
        onClose={() => setPwdRotate(null)}
        onSubmit={handleRotate}
        isSubmitting={isRotating}
      />
      <DeleteConfirmModal
        userId={deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}

function AddUserDrawer({ open, onClose, onSubmit, isSubmitting }) {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleClose = () => {
    setUserId("");
    setPassword("");
    setError("");
    onClose();
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!userId.trim()) {
      setError("User ID zorunlu");
      return;
    }
    if (password.length < 8) {
      setError("Şifre en az 8 karakter olmalı");
      return;
    }
    setError("");
    onSubmit({ user_id: userId.trim(), password });
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Yeni EMQX Kullanıcısı"
      subtitle="MQTT broker authentication kullanıcısı"
      titleIcon={<FaUserPlus className="w-5 h-5 text-indigo-600" />}
      width="md"
    >
      <Drawer.Body className="px-5 py-5">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">User ID</label>
            <TextInput
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="ör. BYN26180001"
              autoFocus
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Cihaz bazlı kullanıcı için cihazın <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">serialNo</code>'sunu kullan.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Şifre</label>
            <TextInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="En az 8 karakter"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Bu şifre EMQX broker'a bağlanırken kullanılacak. İyi yedeğini al.
            </p>
          </div>
          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
              <FaExclamationTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </form>
      </Drawer.Body>
      <Drawer.Footer className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          İptal
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md disabled:opacity-60"
        >
          {isSubmitting ? "Oluşturuluyor..." : "Oluştur"}
        </button>
      </Drawer.Footer>
    </Drawer>
  );
}

function RotatePasswordModal({ userId, onClose, onSubmit, isSubmitting }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  if (!userId) return null;

  const handleSubmit = () => {
    if (password.length < 8) {
      setError("Şifre en az 8 karakter olmalı");
      return;
    }
    setError("");
    onSubmit(password);
    setPassword("");
  };

  return (
    <Modal open={!!userId} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <FaKey className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Şifre Değiştir</h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{userId}</p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-700 mb-3">
          Bu kullanıcı için yeni şifre belirle. Eski şifre geçersiz olur.
        </p>
        <TextInput
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Yeni şifre (8+ karakter)"
          autoFocus
        />
        {error && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
            <FaExclamationTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          İptal
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
        >
          {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function DeleteConfirmModal({ userId, onClose, onConfirm, isDeleting }) {
  if (!userId) return null;

  return (
    <Modal open={!!userId} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
            <FaTrashAlt className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Kullanıcıyı Sil</h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{userId}</p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-700">
          Bu kullanıcıyı silmek istediğinden emin misin?
        </p>
        <p className="mt-2 text-xs text-gray-500">
          Bu kullanıcı ile bağlanan cihaz(lar) bir sonraki bağlantı denemesinde reddedilecek.
        </p>
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          İptal
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isDeleting}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60"
        >
          {isDeleting ? "Siliniyor..." : "Sil"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
